import { useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { platform } from '../../platform';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { record, rows, text } from '../studio/studio-content';
import { resultRows } from './reels-result-model';
import { openStudioTarget } from '../studio/studio-navigation';
import { usePhotoJob } from '../photo/use-photo-job';
import { plural, type ReelsMode } from './reels-policy';
import { MORE_NOTES, postMore } from './reels-more';

/** Хвост «Рилса» (APP-SPEC-5 §8.4, §16): этапы, ряды по 3 варианта, «Ещё 3 варианта» дозаказом,
    «Скачать», «Ещё вариант», «Что дальше», «Открыть в чате»; отказ проверки — «Заменить фото». */
export function ReelsOrderResult({
  state,
  mode,
  title,
  card,
  morePrice,
  onOtherTemplate,
}: {
  state: CircleFormState;
  mode: ReelsMode | 'legacy';
  title: string;
  /** «Обложка Vogue · Вы · 0:10 · Сторис» */
  card: string;
  /** Цена «Ещё 3 варианта» по ряду; null — дозаказ форма не публикует */
  morePrice(group: string): number | null;
  onOtherTemplate?(): void;
}) {
  usePhotoJob(state, 'reels');
  const [moreBusy, setMoreBusy] = useState('');
  const [note, setNote] = useState('');
  const { order, orderId, busy, submissionBlocked } = state;
  if (!orderId) return null;
  const status = order?.status;
  const many = mode === 'multi' || mode === 'pack';
  const lines = resultRows(state);
  const count = lines.reduce((sum, r) => sum + r.items.length, 0);
  const steps = rows(record(order).steps);
  const stepIndex = steps.findIndex(step => step.status === 'running');
  const moderation = /moderation|rejected/.test(order?.error_code ?? '') || record(record(order).error).kind === 'moderation';
  const amount = order?.financial?.amount_tokens;
  const back = async () => {
    if (await state.returnToForm()) document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id: orderId } }));
  };
  const more = async (group: string) => {
    const price = morePrice(group);
    if (!order || price === null || moreBusy) return;
    setMoreBusy(group);
    setNote('');
    try {
      /* ключ, владелец и запрос — общее ядро с движком (reels-more.ts): тот же ключ дозаказа после отката */
      const outcome = await postMore({ orderId: order.id, group, price, owner: state.intent?.owner });
      if (outcome === 'sent') await state.refreshStatus();
      else setNote(MORE_NOTES[outcome]);
    } catch {
      setNote(MORE_NOTES.unknown);
    } finally {
      setMoreBusy('');
    }
  };
  const heading =
    status === 'done'
      ? mode === 'pack'
        ? uiFormat('{0} {1}', count, plural(count, 'рилс готов', 'рилса готовы', 'рилсов готовы'))
        : mode === 'multi'
          ? uiFormat('{0} {1}', lines.length, plural(lines.length, 'рилс готов', 'рилса готовы', 'рилсов готовы'))
          : uiFormat('{0} {1}', count, plural(count, 'вариант готов', 'варианта готовы', 'вариантов готовы'))
      : status === 'failed'
        ? moderation
          ? 'Фото не прошли проверку'
          : 'Не удалось выполнить'
        : status === 'uncertain'
          ? 'Уточняем результат'
          : mode === 'legacy'
            ? 'Создаём рилс'
            : many
              ? 'Собираем рилсы'
              : 'Собираем рилс';
  return (
    <section className="video-order rl-res" aria-live="polite">
      <p className="rl-rh">{title}</p>
      <h3>{uiText(heading)}</h3>
      {(status === 'queued' || status === 'running' || !status) && (
        <>
          <p>{uiText(many ? 'Можно уйти с экрана: бот пришлёт готовые ролики в чат.' : 'Можно уйти с экрана: бот пришлёт готовый ролик в чат.')}</p>
          {steps.length > 0 && (
            <ol className="rl-steps">
              {steps.map((step, i) => (
                <li key={text(step.key) || i} className={text(step.status)}>
                  {text(step.status) === 'done' ? text(step.title_done) || text(step.title) : text(step.title)}
                </li>
              ))}
            </ol>
          )}
          {stepIndex >= 0 && <p className="mono">{`${stepIndex + 1} / ${steps.length}`}</p>}
        </>
      )}
      <p>{uiFormat('Заказ №{0}', orderId)}</p>
      {status === 'uncertain' && <p>{uiText('Результат пока не подтверждён. Проверьте этот заказ, прежде чем создавать новый.')}</p>}
      {status === 'failed' && (
        <p>
          {uiText(
            moderation
              ? uiFormat('Политику и запрещённые темы не пропускаем. {0} рилс не создан.', amount ? uiFormat('{0} C списаны,', amount) : '')
              : order?.financial?.state === 'refunded'
                ? 'Сбой на нашей стороне. Circle вернулись на баланс.'
                : 'Сбой на нашей стороне. Попробуйте ещё раз.',
          )}
        </p>
      )}
      <button type="button" disabled={busy || submissionBlocked} onClick={() => void state.refreshStatus()}>
        {uiText('Проверить статус заказа')}
      </button>
      {status === 'done' && !order?.result?.available && <p>{uiText('Результат недоступен. Проверьте историю заказа.')}</p>}
      {status === 'done' && order?.result?.available && (
        <>
          <p>
            {uiText(
              (mode === 'multi' ? 'У каждого по 3 варианта. ' : '') +
                'Бот уже прислал их в чат, в отдельную тему.' +
                (mode !== 'pack' && lines.some(r => morePrice(r.id) !== null) ? ' Ещё варианты можно добавить в конце ряда.' : ''),
            )}
          </p>
          {lines.map(line => (
            <div key={line.id || 'one'} className="rl-rg">
              {many && line.title && (
                <div className="glbl">
                  {line.title}
                  <span className="mono">{line.items.length}</span>
                </div>
              )}
              <div className="rl-rr">
                {line.items.map((item, i) => (
                  <button key={item.index} type="button" className="rl-v" disabled={busy || submissionBlocked} onClick={() => void state.openResult(item.index)}>
                    <b>{uiFormat('Вариант {0}', i + 1)}</b>
                    {item.caption && <small>{item.caption}</small>}
                  </button>
                ))}
                {mode !== 'pack' && morePrice(line.id) !== null && (
                  <button type="button" className="rl-v more" disabled={!!moreBusy || busy} onClick={() => void more(line.id)}>
                    <b>{uiText(moreBusy === line.id ? 'Собираем…' : 'Ещё 3 варианта')}</b>
                    <small className="mono">{uiFormat('{0} C', morePrice(line.id)!)}</small>
                  </button>
                )}
              </div>
            </div>
          ))}
          {note && <p role="alert">{uiText(note)}</p>}
          {card && (
            <div className="rl-cd">
              <b>{card}</b>
              <small>{uiText('Музыка и монтаж из шаблона')}</small>
            </div>
          )}
        </>
      )}
      {state.result && (
        <div className="video-result">
          {state.result.mime.startsWith('video/') && <video controls playsInline src={state.result.url} />}
          <a href={state.result.url} download={state.result.name}>
            {uiText('Скачать')}
          </a>
        </div>
      )}
      {status === 'done' && (
        <div className="rl-acts">
          <button type="button" disabled={busy || submissionBlocked} onClick={() => void back()}>
            {uiText('Ещё вариант')}
          </button>
          <h4>{uiText('Что дальше')}</h4>
          {onOtherTemplate && (
            <button
              type="button"
              disabled={busy || submissionBlocked}
              onClick={async () => {
                if (await state.returnToForm()) {
                  document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id: orderId } }));
                  onOtherTemplate();
                }
              }}
            >
              {uiText('Другой шаблон')}
            </button>
          )}
          <button type="button" onClick={() => openStudioTarget({ screen: 'circle', who: 'video' })}>
            {uiText('Сделать кружок')}
          </button>
          {platform.kind === 'telegram' && (
            <button type="button" className="btn" onClick={() => platform.close()}>
              {uiText('Открыть в чате')}
            </button>
          )}
        </div>
      )}
      {status === 'failed' && (
        <div className="rl-acts">
          <button type="button" className="btn" disabled={busy || submissionBlocked} onClick={() => void back()}>
            {uiText(moderation ? 'Заменить фото' : 'Попробовать ещё раз')}
          </button>
          {!moderation && (
            <button type="button" className="lnk" disabled={busy || submissionBlocked} onClick={() => void back()}>
              {uiText('К настройкам')}
            </button>
          )}
        </div>
      )}
      {order?.financial?.state === 'dev_free' && <p>{uiText('Тестовый заказ без списания')}</p>}
      {order?.financial?.state === 'refunded' && <p>{uiText('Списание возвращено')}</p>}
      {order && amount && order.financial?.state !== 'dev_free' && order.financial?.state !== 'refunded' && status === 'done' && (
        <p className="rl-rc mono">{uiFormat('Списано {0} C', amount)}</p>
      )}
    </section>
  );
}
